import { CreditCard, Pix } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Alert, Avatar, Box, Chip, CircularProgress, Divider, Grid, IconButton, Modal, Typography } from "@mui/material";
import { format, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useEffect, useMemo, useRef, useState } from "react";
import { ISetup, FinancialService } from "../../../../shared/services/api/financial/FinancialService";
import { globalColorTextSetup, globalMaskDocument, globalTranslatePayment, globalTranslateStatusTaxSetup } from "../../../../shared/utils";
import { useExportPDFCanvas } from "../../hooks/useExportPDFCanvas";

interface IModalDetailSetupProps {
	rowSetup: ISetup;
	isOpenModal: boolean;
	isCloseModal?: () => void;
	overrideWidthValue?: string;
	overrideHeightValue?: string;
	onCloseModal?: (event?: {}, reason?: "backdropClick" | "escapeKeyDown") => void;
}

const style = {
	position: "relative" as const,
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "min(960px, 92vw)",
	height: "auto",
	maxWidth: "92vw",
	maxHeight: "85%",
	bgcolor: "background.paper",
	border: "1px solid #605e5e",
	boxShadow: 24,
	borderRadius: "12px",
	display: "flex",
	flexDirection: "column" as const,
	backgroundColor: "#FFF",
	padding: 3,
};

type DetailItemProps = {
	label: string;
	value?: string | null;
	multiline?: boolean;
	highlight?: boolean;
};

const DetailItem = ({ label, value = "-", multiline = false, highlight = false }: DetailItemProps) => (
	<Box sx={{ py: 1.1 }}>
		<Typography variant="caption" sx={{ color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
			{label}
		</Typography>
		<Typography
			variant="body2"
			sx={{
				mt: 0.4,
				fontWeight: highlight ? 600 : 500,
				color: "#1f2937",
				whiteSpace: multiline ? "pre-wrap" : "normal",
				wordBreak: "break-word",
			}}
		>
			{value || "-"}
		</Typography>
	</Box>
);

const SectionTitle = ({ title, subtitle }: { title: string; subtitle?: string }) => (
	<Box sx={{ mb: 1.5 }}>
		<Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#111827" }}>
			{title}
		</Typography>
		{subtitle && (
			<Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
				{subtitle}
			</Typography>
		)}
	</Box>
);

export const ModalDetailSetup: React.FC<IModalDetailSetupProps> = ({ rowSetup, isOpenModal, onCloseModal, overrideWidthValue, overrideHeightValue }) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const { exportPDF } = useExportPDFCanvas();
	const [isLoading, setIsLoading] = useState(false);
	const [setupDetail, setSetupDetail] = useState<ISetup | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);

	const handleExportPDF = () => {
		if (contentRef.current) {
			exportPDF(contentRef.current, "taxa-setup.pdf");
		}
	};

	useEffect(() => {
		if (!isOpenModal || !rowSetup?.id) return;

		setIsLoading(true);
		setLoadError(null);

		FinancialService.getSetupById(rowSetup.id)
			.then((result) => {
				if (result instanceof Error) {
					setLoadError(result.message);
					return;
				}

				setSetupDetail(result);
			})
			.finally(() => setIsLoading(false));
	}, [isOpenModal, rowSetup?.id]);

	const detail = useMemo(() => setupDetail ?? rowSetup, [setupDetail, rowSetup]);

	const formatWithOffset = (dateStr?: string | null, withTime = false) => {
		if (!dateStr) return "-";
		let date: Date;
		if (dateStr.includes("T")) {
			date = new Date(dateStr);
		} else {
			date = new Date(`${dateStr}T03:00:00`);
		}

		if (!isValid(date)) return "-";

		return format(date, withTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy", { locale: ptBR });
	};

	const formatCurrency = (value?: string | number | null) => {
		const numericValue = Number(value ?? 0);
		if (!Number.isFinite(numericValue)) return "-";
		return numericValue.toLocaleString("pt-br", { style: "currency", currency: "BRL" });
	};

	const primaryAmount = formatCurrency(detail.raw_amount);
	const paidAmount = formatCurrency(detail.credit_amount ?? detail.credited_amount);
	const clientAmount = formatCurrency(detail.client_amount);
	const setupTransferAmount = formatCurrency(detail.setupTransfer);

	// ✅ FUNÇÃO PARA RETORNAR O ÍCONE BASEADO NO MÉTODO DE PAGAMENTO
	const getPaymentIcon = (method: string) => {
		switch (method?.toLowerCase()) {
			case "pix":
				return <Pix sx={{ color: "#7bdcb5" }} />;
			case "credit_card":
			case "boleto_credit_card": // Assumindo que boleto via cartão entra aqui ou você pode criar um específico
				return <CreditCard fontSize="small" color="action" />;

			default:
				return null;
		}
	};

	return (
		<Modal open={isOpenModal} onClose={onCloseModal}>
			<Box
				sx={{
					...style,
					width: overrideWidthValue || style.width,
					height: overrideHeightValue || style.height,
				}}
			>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Typography variant="h5" color="primary">
						Detalhes da taxa setup
					</Typography>
					<Box display="flex" gap={1}>
						<IconButton onClick={handleExportPDF} size="small">
							<PictureAsPdfIcon fontSize="small" />
						</IconButton>
						<IconButton onClick={onCloseModal} size="small">
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>
				</Box>

				<Box ref={contentRef} data-export-pdf="true" sx={{ overflowY: "auto", pr: 1 }}>
							{isLoading && (
								<Box display="flex" justifyContent="center" py={6}>
									<CircularProgress />
								</Box>
							)}

							{loadError && !isLoading && <Alert severity="error">{loadError}</Alert>}

							{!isLoading && !loadError && (
								<>
									<Typography
										variant="h6"
										align="center"
										sx={{
											display: "none",
											"@media print": { display: "block" },
											mb: 3,
											fontWeight: 700,
											textTransform: "uppercase",
											color: "primary.main",
										}}
									>
										Detalhamento de Taxa de Setup
									</Typography>

									<Box
										sx={{
											background: "linear-gradient(135deg, rgba(1,112,179,0.10) 0%, rgba(1,112,179,0.03) 100%)",
											border: "1px solid rgba(1,112,179,0.12)",
											borderRadius: "16px",
											p: { xs: 2, md: 2.5 },
											mb: 3,
										}}
									>
										<Box display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={2}>
											<Box display="flex" alignItems="center" gap={1.5}>
												<Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
													{detail.tenant_name?.trim()?.charAt(0) || "C"}
												</Avatar>
												<Box>
													<Typography variant="h6" sx={{ fontWeight: 700, color: "#111827" }}>
														{detail.tenant_name}
													</Typography>
													<Typography variant="body2" sx={{ color: "#6b7280" }}>
														{detail.client_name} | {globalMaskDocument(detail.tenant_document ?? "")}
													</Typography>
												</Box>
											</Box>
											<Box display="flex" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5} flexDirection={{ xs: "column", md: "row" }}>
												<Chip
													label={globalTranslateStatusTaxSetup(detail.status)}
													variant="outlined"
													sx={{ color: globalColorTextSetup(detail.status), borderColor: globalColorTextSetup(detail.status), fontWeight: 600 }}
												/>
												<Box sx={{ px: 1.75, py: 1, borderRadius: "999px", bgcolor: "primary.main", color: "#fff", fontWeight: 700, minWidth: 150, textAlign: "center" }}>
													{primaryAmount}
												</Box>
											</Box>
										</Box>

										<Grid container spacing={2} sx={{ mt: 1.5 }}>
											<Grid item xs={12} md={4}>
												<DetailItem label="Forma de pagamento" value={globalTranslatePayment(detail.payment_method ?? "")} />
											</Grid>
											<Grid item xs={12} md={4}>
												<DetailItem label="Vencimento" value={formatWithOffset(detail.dueDate)} />
											</Grid>
											<Grid item xs={12} md={4}>
												<DetailItem label="Número" value={String(detail.number ?? "-")} />
											</Grid>
										</Grid>
									</Box>

									<Grid container spacing={3}>
										<Grid item xs={12} md={6}>
											<SectionTitle title="Partes" subtitle="Informações principais do cliente e da imobiliária" />
											<DetailItem label="Imobiliária" value={detail.client_name} highlight />
											<DetailItem label="Documento da imobiliária" value={globalMaskDocument(detail.client_document ?? "")} />
											<DetailItem label="Cliente" value={detail.tenant_name} highlight />
											<DetailItem label="Documento do cliente" value={globalMaskDocument(detail.tenant_document ?? "")} />
											<DetailItem label="E-mail" value={detail.tenant_email || "-"} />
										</Grid>

										<Grid item xs={12} md={6}>
											<SectionTitle title="Cobrança" subtitle="Identificação e rastreabilidade da taxa" />
											<DetailItem label="ID" value={detail.id} />
											<DetailItem label="ID da transação" value={detail.transaction_id} />
											<DetailItem label="ID da análise" value={detail.analysis_id} />
											<DetailItem label="Criado em" value={formatWithOffset(detail.created_at, true)} />
											<DetailItem label="Atualizado em" value={formatWithOffset(detail.updated_at, true)} />
										</Grid>

										<Grid item xs={12} md={6}>
											<SectionTitle title="Valores" subtitle="Resumo financeiro da cobrança" />
											<DetailItem label="Valor bruto" value={primaryAmount} highlight />
											<DetailItem label="Taxa administrativa" value={formatCurrency(detail.administrative_amount)} />
											<DetailItem label="Valor gateway" value={formatCurrency(detail.gateway_amount)} />
											<DetailItem label="Valor baixado" value={paidAmount} />
											<DetailItem label="Repasse imobiliária" value={clientAmount} />
											<DetailItem label="Setup transfer" value={setupTransferAmount} />
										</Grid>

										<Grid item xs={12} md={6}>
											<SectionTitle title="Ocorrências" subtitle="Baixa, cancelamento e observações operacionais" />
											<DetailItem label="Data de pagamento" value={formatWithOffset(detail.payment_date, true)} />
											<DetailItem label="Data de cancelamento" value={formatWithOffset(detail.canceled_at, true)} />
											<DetailItem label="Data de repasse" value={formatWithOffset(detail.client_paid_data)} />
											<DetailItem label="Motivo" value={detail.reason || "-"} multiline />
											<DetailItem label="Observação" value={detail.note || "-"} multiline />
										</Grid>
									</Grid>
								</>
							)}
				</Box>
			</Box>
		</Modal>
	);
};
